import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Button,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import adminApi, { type LLMUsageResponse, type PlanLimitStatus } from '@/services/admin.api';
import BillingTimeframeSelect, {
  timeframeParams,
  useBillingTimeframe,
} from '@/components/billing/BillingTimeframeSelect';
import { formatTokensCompact, formatTokensExact } from '@/utils/formatTokens.util';

type LLMUsage = LLMUsageResponse;

interface LimitState {
  used: number;
  limit: number;
  remaining: number;
  exceeded: boolean;
  usagePercent: number;
}

interface UsageLimits {
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  message: LimitState;
  storage: LimitState;
  apiCalls: LimitState;
  isAnyLimitExceeded: boolean;
}

function bytesToGB(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 10) return `${Math.round(gb)} GB`;
  return `${gb.toFixed(2)} GB`;
}

const BillingAmount: React.FC = () => {
  const [usage, setUsage] = useState<LLMUsage>({
    tokensUsed: 0,
    totalCost: 0,
    requestCount: 0,
    aiMessageCount: 0,
    avgTokensPerRequest: 0,
    modelBreakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<UsageLimits | null>(null);
  const [aiTokensLimit, setAiTokensLimit] = useState<PlanLimitStatus | null>(null);
  const [timeframe, setTimeframe] = useBillingTimeframe();

  // Allowances always describe the CURRENT billing period (that's what the
  // quota gate enforces), so they're fetched once, independent of the time frame.
  useEffect(() => {
    // Settled independently — unrelated data sources, so one failing (e.g. a
    // plan/module restriction) must not blank the other's card too.
    void Promise.allSettled([adminApi.getBillingUsageLimits(), adminApi.getUsageStatus()]).then(
      ([limitsResult, statusResult]) => {
        if (limitsResult.status === 'fulfilled') setLimits(limitsResult.value);
        if (statusResult.status === 'fulfilled') setAiTokensLimit(statusResult.value.aiTokens ?? null);
      }
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchUsage = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await adminApi.getLLMUsage(timeframeParams(timeframe));
        if (!cancelled) setUsage(result);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load AI usage');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    };

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [timeframe]);

  if (loading && !loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" mb={1}>
            Billing Amount
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Full billing summary for AI usage, tokens, requests, and model-wise spend.
          </Typography>
        </Box>
        <BillingTimeframeSelect
          value={timeframe}
          onChange={setTimeframe}
          currentPeriod={usage.currentPeriod}
          loadPeriods={async () => (await adminApi.getUsagePeriods({ page: 1, limit: 12 })).data}
        />
      </Stack>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {limits && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Subscription & Limits
            </Typography>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Monthly Subscription
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {limits.currency} {limits.monthlyPrice}/month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Yearly Subscription
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {limits.currency} {limits.yearlyPrice}/year
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary">Total Message Limit</Typography>
                    <Typography fontWeight="bold">
                      {limits.message.limit.toLocaleString()}
                    </Typography>
                    <Typography color="text.secondary" mt={1}>
                      Used Message
                    </Typography>
                    <Typography
                      fontWeight="bold"
                      color={limits.message.exceeded ? 'error.main' : 'text.primary'}
                    >
                      {limits.message.used.toLocaleString()}
                    </Typography>
                    {limits.message.exceeded && (
                      <Stack spacing={1} mt={1.5}>
                        <Alert severity="error">Your message limit is exceeded.</Alert>
                        <Button variant="outlined" size="small" href="/admin/billing/active-plan">
                          Request More Message Access
                        </Button>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary">Total Storage Limit</Typography>
                    <Typography fontWeight="bold">{bytesToGB(limits.storage.limit)}</Typography>
                    <Typography color="text.secondary" mt={1}>
                      Used Storage
                    </Typography>
                    <Typography
                      fontWeight="bold"
                      color={limits.storage.exceeded ? 'error.main' : 'text.primary'}
                    >
                      {bytesToGB(limits.storage.used)}
                    </Typography>
                    {limits.storage.exceeded && (
                      <Stack spacing={1} mt={1.5}>
                        <Alert severity="error">Your storage limit is full.</Alert>
                        <Button variant="outlined" size="small" href="/admin/billing/active-plan">
                          Request More Storage
                        </Button>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography color="text.secondary">Total APIs Call Limit</Typography>
                    <Typography fontWeight="bold">
                      {limits.apiCalls.limit.toLocaleString()}
                    </Typography>
                    <Typography color="text.secondary" mt={1}>
                      Used APIs Call
                    </Typography>
                    <Typography
                      fontWeight="bold"
                      color={limits.apiCalls.exceeded ? 'error.main' : 'text.primary'}
                    >
                      {limits.apiCalls.used.toLocaleString()}
                    </Typography>
                    {limits.apiCalls.exceeded && (
                      <Stack spacing={1} mt={1.5}>
                        <Alert severity="error">Your API calls limit is exceeded.</Alert>
                        <Button variant="outlined" size="small" href="/admin/billing/active-plan">
                          Request More APIs Call
                        </Button>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {aiTokensLimit && (
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary">AI Tokens / Period</Typography>
                      <Tooltip title={aiTokensLimit.unbounded ? '' : formatTokensExact(aiTokensLimit.limit)}>
                        <Typography fontWeight="bold">
                          {aiTokensLimit.unbounded ? 'Unlimited' : formatTokensCompact(aiTokensLimit.limit)}
                        </Typography>
                      </Tooltip>
                      <Typography color="text.secondary" mt={1}>
                        Used AI Tokens
                      </Typography>
                      <Tooltip title={formatTokensExact(aiTokensLimit.used)}>
                        <Typography
                          fontWeight="bold"
                          color={aiTokensLimit.exceeded ? 'error.main' : 'text.primary'}
                        >
                          {formatTokensCompact(aiTokensLimit.used)}
                        </Typography>
                      </Tooltip>
                      {aiTokensLimit.exceeded && (
                        <Stack spacing={1} mt={1.5}>
                          <Alert severity="error">Your AI token allowance is used up.</Alert>
                          <Button variant="outlined" size="small" href="/admin/billing/active-plan">
                            Request More AI Tokens
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              Allowances always apply to the current billing period.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total AI Billing Amount
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                ${usage.totalCost.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Tokens Used
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {formatTokensCompact(usage.tokensUsed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTokensExact(usage.tokensUsed)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total AI Requests
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {usage.requestCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                AI Assistant Messages
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {usage.aiMessageCount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Average Tokens per Request
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatTokensCompact(usage.avgTokensPerRequest)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Model-wise Billing Breakdown
              </Typography>
              {usage.modelBreakdown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No model usage data found yet.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usage.modelBreakdown.map((item) => (
                        <TableRow key={item.model}>
                          <TableCell>{item.model}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={formatTokensExact(item.tokens)}>
                              <span>{formatTokensCompact(item.tokens)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">${item.cost.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingAmount;
